import React, { useState, Dispatch, SetStateAction, useEffect } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";

import {
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { tableDataType } from "src/app/components/Table/Table";
import { ImageOption } from "src/app/components/CustomSelect/CustomSelect";
import { RootState } from "src/redux/reducers";
import { ReactComponent as SettingsIcon } from "../../../images/settings.svg";
import { ReactComponent as SettingsShapeIcon } from "../../../images/settings-dropdown/settings-shape.svg";
import { ReactComponent as LayoutIcon } from "../../../images/settings-dropdown/layout.svg";
import { ReactComponent as TableIcon } from "../../../images/settings-dropdown/table.svg";
import { ReactComponent as BoardIcon } from "../../../images/settings-dropdown/board.svg";
import { ReactComponent as ChevronDownIcon } from "../../../images/chevron-down.svg";
import CustomizeFieldsDropdown from "../CustomizeFieldsDropdown/CustomizeFieldsDropdown";
import { ReactComponent as SelectedIcon } from "../../../images/selected.svg";
import useCheckProjectPermission from "src/hooks/useCheckProjectPermission";
import { MetaFieldOptions } from "src/app/Task/Subtask/Subtask";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import {
  CurrentView,
  CurrentViewValue,
} from "src/app/project/SingleProject/types";

import "./TableSettingsDropdown.scss";
import DropdownPortal from "src/app/components/DropdownPortal";

const viewOptions: CurrentView[] = [
  {
    value: "list",
    label: (
      <ImageOption icon={TableIcon}>
        <IDHFormattedMessage id="ws_table" defaultMessage="Table" />
      </ImageOption>
    ),
  },
  {
    value: "board",
    label: (
      <ImageOption icon={BoardIcon}>
        <IDHFormattedMessage id="ws_board" defaultMessage="Board" />
      </ImageOption>
    ),
  },
];

interface MappedTaskColumn {
  id: string;
  name: string;
  isRequired: boolean;
  isVisible: boolean;
  key: string;
  metaFieldOptions: MetaFieldOptions;
  metaFieldSource: string | null;
  rank: string;
  title: string;
  type: MetaFieldType;
  valueSource: string | null;
}

interface TableSettingsDropdownProps {
  setShowFieldSetupModal: Dispatch<SetStateAction<boolean>>;
  fieldData: any;
  setFieldData: Dispatch<SetStateAction<any>>;
  fields: MappedTaskColumn[];
  currentView: CurrentViewValue;
  setCurrentView: Dispatch<SetStateAction<CurrentViewValue>>;
}

export function TableSettingsDropdown({
  setShowFieldSetupModal,
  fieldData,
  setFieldData,
  fields,
  currentView,
  setCurrentView,
}: TableSettingsDropdownProps) {
  const {
    projectReducer: { taskType },
  } = useSelector((state: RootState) => state);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLayoutOptions, setShowLayoutOptions] = useState(false);

  const hasTaskMetaFieldsManagementPermission = useCheckProjectPermission(
    "TASK_META_FIELDS_MANAGEMENT",
  );

  const renderTabTitle = () => {
    switch (currentView) {
      case "list":
        return "Table";

      case "board":
        return "Board";

      default:
        return currentView;
    }
  };

  useEffect(() => {
    if (!isMenuOpen) {
      setShowLayoutOptions(false);
    }
  }, [isMenuOpen]);

  const menu = (
    <DropdownMenu className="table-settings-dropdown__menu" isOpen={isMenuOpen}>
      <DropdownMenuItem
        className={classNames("table-settings-dropdown__layout", {
          "table-settings-dropdown__layout--active": showLayoutOptions,
        })}
        onClick={() => setShowLayoutOptions((v) => !v)}
      >
        <span className="table-settings-dropdown__layout-wrapper">
          <LayoutIcon />
          <IDHFormattedMessage id="ws_layout" defaultMessage="Layout" />
        </span>
        <span
          className={classNames("table-settings-dropdown__tab-wrapper", {
            "table-settings-dropdown__tab-wrapper--rotated": showLayoutOptions,
          })}
        >
          {renderTabTitle()}
          <ChevronDownIcon />
        </span>
      </DropdownMenuItem>

      <div
        className={classNames("table-settings-dropdown__layout-options", {
          "table-settings-dropdown__layout-options--visible": showLayoutOptions,
        })}
      >
        {viewOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            className="table-settings-dropdown__layout-options-item"
            dark={option.value === currentView}
            onClick={() => {
              setCurrentView(option.value);
              setIsMenuOpen(false);
            }}
          >
            {option.label}
            {option.value === currentView && (
              <SelectedIcon style={{ marginLeft: 8, marginRight: 0 }} />
            )}
          </DropdownMenuItem>
        ))}
      </div>

      {hasTaskMetaFieldsManagementPermission && (
        <CustomizeFieldsDropdown
          setShowFieldSetupModal={(value) => {
            setIsMenuOpen(false);
            setShowFieldSetupModal(value);
          }}
          fieldData={fieldData}
          setFieldData={setFieldData}
          fields={fields}
          dataType={tableDataType.Task}
          taskType={taskType}
          toBottom
          dropdownButton={
            <DropdownMenuItem className="table-settings-dropdown__settings">
              <SettingsShapeIcon />
              <IDHFormattedMessage id="ws_settings" defaultMessage="Settings" />
            </DropdownMenuItem>
          }
          subheader={
            <IDHFormattedMessage id="ws_columns" defaultMessage="Columns" />
          }
        />
      )}
    </DropdownMenu>
  );

  return (
    <DropdownPortal
      className="table-settings-dropdown"
      overlay={menu}
      visible={isMenuOpen}
      onVisibleChange={(v: boolean) => {
        setIsMenuOpen(v);
      }}
      placement="bottomRight"
    >
      <Button variant="light-grey" size="square">
        <SettingsIcon />
      </Button>
    </DropdownPortal>
  );
}
